export interface Point {
  x: number;
  y: number;
}

export interface Contours {
  paths: Array<Array<Point>>;
  symmetry: Symmetry;
}

export interface ChladniParamsSources {
  mode: "sources";
  frequency: number;
  phaseShift: number;
  elasticity: number;
  sources: number;
  quality: number;
  symmetry?: Symmetry | "auto";
}

export interface ChladniParamsDefault {
  mode: "default";
  a: number;
  b: number;
  n: number;
  m: number;
  quality: number;
  symmetry?: Symmetry | "auto";
  origin?: [number, number];
  anchor?: Anchor;
  length?: number;
}

export type ChladniParams = ChladniParamsSources | ChladniParamsDefault;

export type Anchor =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-left"
  | "top-right"
  | "bottom-right"
  | "bottom-left"
  | "center";

export type ChladniParamsWithSize = ChladniParams & {
  width: number;
  height: number;
};

export type Symmetry = "quadrant" | "bilateral" | "none";

// Helper to convert physical dimensions to logical coordinate system
// Uses a fixed 60x60 logical space to ensure pattern consistency
function getLogicalCoords(width: number, height: number, origin?: [number, number], anchor?: Anchor, length?: number) {
  const lSize = 60;
  const aspectRatio = width / height;

  // If custom viewport parameters are not provided, use default behavior
  if (!origin || !anchor || length === undefined) {
    // Calculate how much of the logical space is visible based on aspect ratio
    const lHeight = lSize;
    const lWidth = lSize * aspectRatio;

    return {
      size: lSize,
      width: lWidth,
      height: lHeight,
      scaleX: width / lWidth,
      scaleY: height / lHeight,
      xStart: (lSize - lWidth) / 2,
      yStart: (lSize - lHeight) / 2,
    };
  }

  // Determine which dimension length applies to based on anchor and aspect ratio
  // Length extends to the nearest edge (perpendicular to the anchor edge)
  const isLandscape = aspectRatio > 1;
  const isPortrait = aspectRatio < 1;

  let lengthAppliesToVertical = false;

  if (anchor === "top" || anchor === "bottom") {
    lengthAppliesToVertical = true;
  } else if (anchor === "left" || anchor === "right") {
    lengthAppliesToVertical = false;
  } else if (anchor === "top-left" || anchor === "top-right" || anchor === "bottom-left" || anchor === "bottom-right") {
    if (isLandscape) {
      lengthAppliesToVertical = true;
    } else if (isPortrait) {
      lengthAppliesToVertical = false;
    } else {
      // square
      lengthAppliesToVertical = true;
    }
  } else {
    // center: use aspect ratio to determine
    lengthAppliesToVertical = isLandscape;
  }

  let lWidth: number;
  let lHeight: number;

  if (lengthAppliesToVertical) {
    lHeight = length;
    lWidth = length * aspectRatio;
  } else {
    lWidth = length;
    lHeight = length / aspectRatio;
  }

  // Calculate where the origin appears on the canvas based on anchor
  // First, determine the origin's position in logical space relative to viewport
  let viewportOrigin: [number, number];

  switch (anchor) {
    case "top-left":
      viewportOrigin = [0, 0];
      break;
    case "top-right":
      viewportOrigin = [lWidth, 0];
      break;
    case "bottom-left":
      viewportOrigin = [0, lHeight];
      break;
    case "bottom-right":
      viewportOrigin = [lWidth, lHeight];
      break;
    case "top":
      viewportOrigin = [lWidth / 2, 0];
      break;
    case "bottom":
      viewportOrigin = [lWidth / 2, lHeight];
      break;
    case "left":
      viewportOrigin = [0, lHeight / 2];
      break;
    case "right":
      viewportOrigin = [lWidth, lHeight / 2];
      break;
    case "center":
      viewportOrigin = [lWidth / 2, lHeight / 2];
      break;
  }

  // Calculate viewport bounds in logical space
  // origin[0], origin[1] is where the anchor point maps to in logical space
  // For top-left anchor: viewportOrigin[0]=0, viewportOrigin[1]=0, so viewport starts at origin
  // This ensures the anchor point stays fixed at origin[0], origin[1] regardless of length
  const viewportXStart = origin[0] - viewportOrigin[0];
  const viewportYStart = origin[1] - viewportOrigin[1];

  // Calculate scaling factors
  const scaleX = width / lWidth;
  const scaleY = height / lHeight;

  return {
    size: lSize,
    width: lWidth,
    height: lHeight,
    scaleX,
    scaleY,
    xStart: viewportXStart,
    yStart: viewportYStart,
  };
}

export function sourcesFor(n: number, width: number, height: number, scale = true): Array<Point> {
  const l = getLogicalCoords(width, height);

  // Keep sources at fixed position in logical space (center of the 60x60 grid)
  const cx = l.size / 2;
  const cy = l.size / 2;

  const xStart = scale ? (l.size - l.width) / 2 : 0;
  const yStart = scale ? (l.size - l.height) / 2 : 0;

  const scaleX = scale ? l.scaleX : 1;
  const scaleY = scale ? l.scaleY : 1;

  // Scale radius based on the fixed logical size to keep pattern consistent
  const R = l.size / 3;

  return Array.from({ length: n }, (_, i) => {
    const theta = (i / n) * 2 * Math.PI - Math.PI / 2;
    return { x: (cx + R * Math.cos(theta) - xStart) * scaleX, y: (cy + R * Math.sin(theta) - yStart) * scaleY };
  });
}

// Detect rotational symmetry in source arrangement
function getSourcesSymmetry(params: ChladniParamsSources): Symmetry {
  if (params.sources % 4 === 0) {
    return "quadrant";
  }

  if (params.sources % 2 === 0 || params.sources === 3) {
    return "bilateral";
  }

  return "none";
}

function getSymmetry(params: ChladniParamsDefault): Symmetry {
  const originX = params.origin?.[0] ?? 0;
  const originY = params.origin?.[1] ?? 0;
  const anchor = params.anchor ?? "center";
  if (anchor === "center" && originX === 0 && originY === 0) {
    return "quadrant";
  }
  return "none";
}

function computeField(params: ChladniParamsWithSize, symmetry: Symmetry) {
  const l = getLogicalCoords(
    params.width,
    params.height,
    "origin" in params ? params.origin : undefined,
    "anchor" in params ? params.anchor : undefined,
    "length" in params ? params.length : undefined,
  );

  // Use viewport bounds from getLogicalCoords
  const xStart = l.xStart;
  const yStart = l.yStart;
  const xs = Array.from({ length: params.quality }, (_, i) => xStart + (i * l.width) / (params.quality - 1));
  const ys = Array.from({ length: params.quality }, (_, j) => yStart + (j * l.height) / (params.quality - 1));
  const z = Array.from({ length: ys.length }, () => new Array(xs.length).fill(0));

  if (params.mode === "default") {
    // For default mode, normalize coordinates
    // For custom viewports, scale normalization so larger length shows more pattern
    const hasCustomViewport = params.origin !== undefined && params.anchor !== undefined && params.length !== undefined;

    if (hasCustomViewport) {
      // For custom viewports: map origin to normalized 0, scale by viewport size
      // Default length=0.5 maps to normalized range 0.5, so length=2 should map to range 2.0 (4x more)
      // To show more pattern with larger viewport, map viewport coordinates directly to normalized space
      // normalized = coordinate * scale, so scale = 1.0 maps viewport directly to normalized range
      // This means larger viewports automatically show more of the pattern
      const normalizationScale = 1.0;
      const normalizationOffset = 0; // Origin maps to normalized 0

      if (symmetry !== "none") {
        const halfGrid = Math.ceil(params.quality / 2);
        if (symmetry === "quadrant") {
          for (let j = halfGrid - 1; j < params.quality; j++) {
            const y = ys[j] * normalizationScale + normalizationOffset;
            for (let i = halfGrid - 1; i < params.quality; i++) {
              const x = xs[i] * normalizationScale + normalizationOffset;
              z[j][i] =
                params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
                params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
            }
          }
        } else if (symmetry === "bilateral") {
          for (let j = 0; j < params.quality; j++) {
            const y = ys[j] * normalizationScale + normalizationOffset;
            for (let i = halfGrid - 1; i < params.quality; i++) {
              const x = xs[i] * normalizationScale + normalizationOffset;
              z[j][i] =
                params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
                params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
            }
          }
        }
        return { xs, ys, z };
      }

      for (let j = 0; j < ys.length; j++) {
        const y = ys[j] * normalizationScale + normalizationOffset;
        for (let i = 0; i < xs.length; i++) {
          const x = xs[i] * normalizationScale + normalizationOffset;
          z[j][i] =
            params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
            params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
        }
      }
      return { xs, ys, z };
    }

    // Default behavior: use original normalization
    const normalizationScale = 1 / l.size;
    const normalizationOffset = -0.5;

    if (symmetry !== "none") {
      const halfGrid = Math.ceil(params.quality / 2);
      if (symmetry === "quadrant") {
        // Compute only the bottom-right quadrant (including center lines)
        for (let j = halfGrid - 1; j < params.quality; j++) {
          const y = ys[j] * normalizationScale + normalizationOffset;
          for (let i = halfGrid - 1; i < params.quality; i++) {
            const x = xs[i] * normalizationScale + normalizationOffset;
            z[j][i] =
              params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
              params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
          }
        }
      } else if (symmetry === "bilateral") {
        // Compute only the right half (including center line)
        for (let j = 0; j < params.quality; j++) {
          const y = ys[j] * normalizationScale + normalizationOffset;
          for (let i = halfGrid - 1; i < params.quality; i++) {
            const x = xs[i] * normalizationScale + normalizationOffset;
            z[j][i] =
              params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
              params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
          }
        }
      }

      return { xs, ys, z };
    }

    // Plate equation: a*sin(π*n*x)*sin(π*m*y) + b*sin(π*m*x)*sin(π*n*y)
    // Normalize coordinates using scale factor based on viewport size
    for (let j = 0; j < ys.length; j++) {
      const y = ys[j] * normalizationScale + normalizationOffset;
      for (let i = 0; i < xs.length; i++) {
        const x = xs[i] * normalizationScale + normalizationOffset;
        z[j][i] =
          params.a * Math.sin(Math.PI * params.n * x) * Math.sin(Math.PI * params.m * y) +
          params.b * Math.sin(Math.PI * params.m * x) * Math.sin(Math.PI * params.n * y);
      }
    }

    return { xs, ys, z };
  }

  // Sources mode: use wave interference from sources
  const src = sourcesFor(params.sources, params.width, params.height, false);
  const invV = 1 / params.elasticity;

  if (symmetry !== "none") {
    const TWO_PI_OVER_SIZE = (2 * Math.PI) / l.size;
    const halfGrid = Math.ceil(params.quality / 2);

    if (symmetry === "quadrant") {
      // Compute only the bottom-right quadrant (including center lines)
      for (let j = halfGrid - 1; j < params.quality; j++) {
        const y = ys[j];
        for (let i = halfGrid - 1; i < params.quality; i++) {
          const x = xs[i];

          let s = 0;
          for (let k = 0; k < src.length; k++) {
            const srcDx = x - src[k].x;
            const srcDy = y - src[k].y;
            const r = Math.hypot(srcDx, srcDy);
            s += Math.sin(TWO_PI_OVER_SIZE * params.frequency * (params.phaseShift - invV * r));
          }
          z[j][i] = s;
        }
      }
    } else if (symmetry === "bilateral") {
      // Compute only the right half (including center line)
      for (let j = 0; j < params.quality; j++) {
        const y = ys[j];
        for (let i = halfGrid - 1; i < params.quality; i++) {
          const x = xs[i];

          let s = 0;
          for (let k = 0; k < src.length; k++) {
            const srcDx = x - src[k].x;
            const srcDy = y - src[k].y;
            const r = Math.hypot(srcDx, srcDy);
            s += Math.sin(TWO_PI_OVER_SIZE * params.frequency * (params.phaseShift - invV * r));
          }
          z[j][i] = s;
        }
      }
    }

    return { xs, ys, z };
  }

  // Use fixed logical size for wave calculation to keep pattern consistent
  const TWO_PI_OVER_SIZE = (2 * Math.PI) / l.size;

  // Original computation for cases without exploitable symmetry
  for (let j = 0; j < ys.length; j++) {
    const y = ys[j];
    for (let i = 0; i < xs.length; i++) {
      const x = xs[i];
      let s = 0;
      for (let k = 0; k < src.length; k++) {
        const dx = x - src[k].x;
        const dy = y - src[k].y;
        const r = Math.hypot(dx, dy);
        s += Math.sin(TWO_PI_OVER_SIZE * params.frequency * (params.phaseShift - invV * r));
      }
      z[j][i] = s;
    }
  }

  return { xs, ys, z };
}

// Enhanced contour finding that can work on specific sectors for optimization
function findContours(
  params: ChladniParamsWithSize,
  xs: number[],
  ys: number[],
  z: number[][],
  level: number,
  symmetry: Symmetry,
) {
  const l = getLogicalCoords(
    params.width,
    params.height,
    "origin" in params ? params.origin : undefined,
    "anchor" in params ? params.anchor : undefined,
    "length" in params ? params.length : undefined,
  );

  const rows = z.length;
  const cols = z[0].length;

  // Use viewport offset from getLogicalCoords to transform logical coords back to physical coords
  const xStart = l.xStart;
  const yStart = l.yStart;

  // First, collect all contour segments
  const segments: Array<[Point, Point]> = [];

  function lerp(v1: number, v2: number, p1: number, p2: number) {
    if (Math.abs(v1 - v2) < 1e-10) return (p1 + p2) / 2;
    return p1 + ((level - v1) * (p2 - p1)) / (v2 - v1);
  }

  // Determine which region to process
  let iStart = 0;
  const iEnd = cols - 1;
  let jStart = 0;
  const jEnd = rows - 1;

  if (symmetry === "quadrant") {
    // Process only the bottom-right quadrant for primary path creation
    const halfGrid = Math.ceil(rows / 2);
    jStart = halfGrid - 1;
    iStart = halfGrid - 1;
  } else if (symmetry === "bilateral") {
    // Process only the right half for primary path creation
    const halfGrid = Math.ceil(cols / 2);
    iStart = halfGrid - 1;
  }

  // Generate segments using marching squares
  for (let i = jStart; i < jEnd; i++) {
    for (let j = iStart; j < iEnd; j++) {
      const v1 = z[i][j]; // top-left
      const v2 = z[i][j + 1]; // top-right
      const v3 = z[i + 1][j + 1]; // bottom-right
      const v4 = z[i + 1][j]; // bottom-left

      // Create marching squares configuration
      let config = 0;
      if (v1 > level) config |= 1;
      if (v2 > level) config |= 2;
      if (v3 > level) config |= 4;
      if (v4 > level) config |= 8;

      if (config === 0 || config === 15) continue;

      const x1 = xs[j];
      const x2 = xs[j + 1];
      const y1 = ys[i];
      const y2 = ys[i + 1];

      // Calculate interpolated points on edges, transforming from offset logical coords to physical coords
      const topEdge = { x: (lerp(v1, v2, x1, x2) - xStart) * l.scaleX, y: (y1 - yStart) * l.scaleY };
      const rightEdge = { x: (x2 - xStart) * l.scaleX, y: (lerp(v2, v3, y1, y2) - yStart) * l.scaleY };
      const bottomEdge = { x: (lerp(v4, v3, x1, x2) - xStart) * l.scaleX, y: (y2 - yStart) * l.scaleY };
      const leftEdge = { x: (x1 - xStart) * l.scaleX, y: (lerp(v1, v4, y1, y2) - yStart) * l.scaleY };

      // Connect edges based on configuration
      switch (config) {
        case 1:
        case 14:
          segments.push([leftEdge, topEdge]);
          break;
        case 2:
        case 13:
          segments.push([topEdge, rightEdge]);
          break;
        case 3:
        case 12:
          segments.push([leftEdge, rightEdge]);
          break;
        case 4:
        case 11:
          segments.push([rightEdge, bottomEdge]);
          break;
        case 5:
          segments.push([leftEdge, topEdge]);
          segments.push([rightEdge, bottomEdge]);
          break;
        case 6:
        case 9:
          segments.push([topEdge, bottomEdge]);
          break;
        case 7:
        case 8:
          segments.push([leftEdge, bottomEdge]);
          break;
        case 10:
          segments.push([topEdge, leftEdge]);
          segments.push([rightEdge, bottomEdge]);
          break;
      }
    }
  }

  // Now connect segments into continuous paths
  const paths: Array<Array<Point>> = [];
  const used = new Set();

  for (let i = 0; i < segments.length; i++) {
    if (used.has(i)) continue;

    const path: Array<Point> = [segments[i][0], segments[i][1]];
    used.add(i);

    let changed = true;
    while (changed) {
      changed = false;

      for (let j = 0; j < segments.length; j++) {
        if (used.has(j)) continue;

        const segment = segments[j];
        const pathStart = path[0];
        const pathEnd = path[path.length - 1];

        const tolerance = 1e-6;

        // Check if segment connects to end of path
        if (Math.abs(segment[0].x - pathEnd.x) < tolerance && Math.abs(segment[0].y - pathEnd.y) < tolerance) {
          path.push(segment[1]);
          used.add(j);
          changed = true;
          break;
        }
        // Check if segment connects to end of path (reversed)
        else if (Math.abs(segment[1].x - pathEnd.x) < tolerance && Math.abs(segment[1].y - pathEnd.y) < tolerance) {
          path.push(segment[0]);
          used.add(j);
          changed = true;
          break;
        }
        // Check if segment connects to start of path
        else if (Math.abs(segment[0].x - pathStart.x) < tolerance && Math.abs(segment[0].y - pathStart.y) < tolerance) {
          path.unshift(segment[1]);
          used.add(j);
          changed = true;
          break;
        }
        // Check if segment connects to start of path (reversed)
        else if (Math.abs(segment[1].x - pathStart.x) < tolerance && Math.abs(segment[1].y - pathStart.y) < tolerance) {
          path.unshift(segment[0]);
          used.add(j);
          changed = true;
          break;
        }
      }
    }

    if (path.length > 1) {
      paths.push(path);
    }
  }

  return paths;
}

export function chladni(params: ChladniParamsWithSize): Contours {
  const symmetry =
    params.symmetry === "auto" || params.symmetry === undefined
      ? params.mode === "default"
        ? getSymmetry(params)
        : getSourcesSymmetry(params)
      : params.symmetry;
  const { xs, ys, z } = computeField(params, symmetry);
  const paths = findContours(params, xs, ys, z, 0, symmetry);
  return { paths, symmetry };
}
